import { useEffect, useRef } from 'react';

/**
 * Custom hook to show toast notifications based on usage thresholds
 * Shows warnings at 80%, 90%, and 100% usage
 */
export function useUsageNotifications(usage) {
  const notifiedThresholds = useRef(new Set());

  useEffect(() => {
    if (!usage || !usage.eventsLimit || usage.eventsLimit === null) {
      // No notifications for unlimited plans or missing usage data
      return;
    }

    const { monthlyEvents, totalEventsAllowed } = usage;
    const usagePercentage = (monthlyEvents / totalEventsAllowed) * 100;

    // Define thresholds
    const thresholds = [
      { level: 80, message: 'You\'ve used 80% of your monthly events', type: 'warning' },
      { level: 90, message: 'You\'ve used 90% of your monthly events', type: 'warning' },
      { level: 100, message: 'You\'ve reached your monthly event limit', type: 'error' }
    ];

    // Check each threshold
    thresholds.forEach(({ level, message, type }) => {
      const thresholdKey = `${level}-${monthlyEvents}-${totalEventsAllowed}`;
      
      if (usagePercentage >= level && !notifiedThresholds.current.has(thresholdKey)) {
        // Show toast notification
        showToast(message, type, level);
        notifiedThresholds.current.add(thresholdKey);
      }
    });

    // Clear old notifications when usage resets (new month)
    if (usagePercentage < 50) {
      notifiedThresholds.current.clear();
    }
  }, [usage]);
}

/**
 * Show a toast notification
 */
function showToast(message, type = 'info', level = 0) {
  // Create toast element
  const toast = document.createElement('div');
  toast.className = `usage-toast usage-toast-${type}`;
  
  const icon = type === 'error' ? '⚠️' : type === 'warning' ? '⚡' : 'ℹ️';
  
  toast.innerHTML = `
    <div class="toast-icon">${icon}</div>
    <div class="toast-content">
      <div class="toast-title">${type === 'error' ? 'Limit Reached' : 'Usage Warning'}</div>
      <div class="toast-message">${message}</div>
      ${level >= 80 ? '<div class="toast-action">Consider upgrading your plan or purchasing an add-on.</div>' : ''}
    </div>
    <button class="toast-close" onclick="this.parentElement.remove()">×</button>
  `;

  // Add to document
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
  }

  container.appendChild(toast);

  // Auto-remove after 8 seconds
  setTimeout(() => {
    if (toast.parentElement) {
      toast.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => toast.remove(), 300);
    }
  }, 8000);

  // Add toast styles if not already present
  if (!document.getElementById('toast-styles')) {
    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
      #toast-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
      }

      .usage-toast {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        animation: slideIn 0.3s ease-out;
        border-left: 4px solid;
      }

      .usage-toast-warning {
        border-left-color: #f59e0b;
        background: #fffbeb;
      }

      .usage-toast-error {
        border-left-color: #ef4444;
        background: #fef2f2;
      }

      .usage-toast-info {
        border-left-color: #3b82f6;
        background: #eff6ff;
      }

      .toast-icon {
        font-size: 24px;
        flex-shrink: 0;
      }

      .toast-content {
        flex: 1;
      }

      .toast-title {
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 4px;
        color: #1f2937;
      }

      .toast-message {
        font-size: 13px;
        color: #4b5563;
        margin-bottom: 4px;
      }

      .toast-action {
        font-size: 12px;
        color: #6b7280;
        margin-top: 8px;
        font-style: italic;
      }

      .toast-close {
        background: none;
        border: none;
        font-size: 24px;
        color: #9ca3af;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: color 0.2s;
      }

      .toast-close:hover {
        color: #4b5563;
      }

      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }

      @media (max-width: 640px) {
        #toast-container {
          left: 20px;
          right: 20px;
          max-width: none;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default useUsageNotifications;
